import { useEffect, useRef, useState } from "react";
import { Check, Copy } from "lucide-react";
import { parseErrorId } from "@/lib/queryClient";

// Task #847: inline counterpart to the Toaster's copy button (Task #773).
// The Toaster auto-detects `\n\nError ID: <id>` in toast descriptions and
// renders a copy chip; non-toast surfaces (inline error banners on
// onboarding / public pages, etc.) need the same affordance so support can
// trace any incident regardless of where the error is shown.

const ERROR_ID_RE = /^([\s\S]*?)\n\nError ID:\s*(\S+)\s*$/;

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the legacy fallback below.
  }
  if (typeof document === "undefined") return false;
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "0";
    ta.style.left = "0";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

export function ErrorIdChip({ errorId }: { errorId: string }) {
  const [copied, setCopied] = useState(false);
  const resetTimerRef = useRef<number | null>(null);
  useEffect(() => {
    return () => {
      if (resetTimerRef.current !== null) {
        window.clearTimeout(resetTimerRef.current);
      }
    };
  }, []);
  return (
    <span className="inline-flex flex-wrap items-center gap-1" data-testid="error-id-chip">
      <span>
        Error ID: <span data-testid="text-error-id-inline">{errorId}</span>
      </span>
      <button
        type="button"
        onClick={async (e) => {
          e.stopPropagation();
          const ok = await copyToClipboard(errorId);
          if (!ok) return;
          setCopied(true);
          if (resetTimerRef.current !== null) {
            window.clearTimeout(resetTimerRef.current);
          }
          resetTimerRef.current = window.setTimeout(() => {
            setCopied(false);
            resetTimerRef.current = null;
          }, 1500);
        }}
        className="ml-1 inline-flex items-center gap-1 rounded border border-current/30 px-1.5 py-0.5 text-xs font-medium opacity-80 hover-elevate active-elevate-2"
        aria-label={copied ? "Copied error ID" : "Copy error ID"}
        data-testid="button-copy-error-id-inline"
      >
        {copied ? (
          <>
            <Check className="h-3 w-3" aria-hidden="true" />
            <span data-testid="text-copy-error-id-inline-status">Copied</span>
          </>
        ) : (
          <>
            <Copy className="h-3 w-3" aria-hidden="true" />
            <span>Copy</span>
          </>
        )}
      </button>
    </span>
  );
}

/**
 * Render a friendly inline error banner that, when an `errorId` is present
 * (either passed directly or extractable from the thrown error), surfaces a
 * copyable chip alongside the user-facing message. The `message` should be
 * the bare user-facing text — callers normally derive it from
 * `parseErrorMessage` and then strip the `\n\nError ID:` suffix, or simply
 * pass the friendly fallback they already had.
 */
export function ErrorBannerWithId({
  message,
  error,
  errorId,
  className,
  testId,
}: {
  message: string;
  error?: unknown;
  errorId?: string | null;
  className?: string;
  testId?: string;
}) {
  // If the message already carries the `\n\nError ID:` suffix produced by
  // `parseErrorMessage`, split it apart so the chip renders cleanly.
  const match = message.match(ERROR_ID_RE);
  const baseMessage = match ? match[1] : message;
  const resolvedId = errorId ?? (match ? match[2] : parseErrorId(error));
  return (
    <div
      className={
        className ??
        "text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3 whitespace-pre-line"
      }
      data-testid={testId ?? "error-banner-with-id"}
    >
      {baseMessage}
      {resolvedId ? (
        <>
          {"\n\n"}
          <ErrorIdChip errorId={resolvedId} />
        </>
      ) : null}
    </div>
  );
}
